import axios from 'axios';

/**
 * A content rejection answers 400 with a body the reviewer can act on, unlike a transport failure.
 * `unauthorized` is its own verdict because signing in again is what fixes it — retrying the same
 * payload never can.
 */
export type ReviewSubmissionErrorKind = 'content-rejected' | 'unauthorized' | 'network' | 'unknown';

export interface ReviewSubmissionError {
  kind: ReviewSubmissionErrorKind;
  message: string;
  flaggedText?: string[];
}

const CONTENT_REJECTED_MESSAGE = 'Some content in this review needs to be updated before it can be published.';
const NETWORK_MESSAGE = 'We could not reach SkinSeoul. Check your connection and try again.';
const UNAUTHORIZED_MESSAGE = 'Your session has expired. Please sign in again to post your review.';
const UNKNOWN_MESSAGE = 'Your review could not be submitted. Please try again.';

/** Field validation answers 400 too, so only this code identifies a moderation rejection. */
const CONTENT_REJECTED_ERROR_CODE = 'REVIEW_POLICY_VIOLATION';

const BAD_REQUEST_STATUS = 400;
const UNAUTHORIZED_STATUS = 401;

interface ReviewRejectionBody {
  errorCode?: string;
  /** Field validation reports one entry per offending field, which is why this can be a list. */
  message?: string | string[];
  reason?: string;
  flaggedText?: string[];
}

/** What the server said is the only text that names the field to fix, so it outranks the generic copy. */
const toServerMessage = (body: ReviewRejectionBody | undefined): string | undefined => {
  const message = Array.isArray(body?.message) ? body?.message.join(' ') : body?.message;
  const reported = message ?? body?.reason;
  return reported?.trim() ? reported : undefined;
};

export const toReviewSubmissionError = (error: unknown): ReviewSubmissionError => {
  if (!axios.isAxiosError(error)) {
    // Media upload rejects with a plain Error naming the file and the rule it broke; flattening it
    // to "Please try again" leaves the reviewer resubmitting the same unsupported file.
    const message = error instanceof Error && error.message.trim() ? error.message : UNKNOWN_MESSAGE;
    return { kind: 'unknown', message };
  }

  if (!error.response) {
    return { kind: 'network', message: NETWORK_MESSAGE };
  }

  const body = error.response.data as ReviewRejectionBody | undefined;

  if (body?.errorCode === CONTENT_REJECTED_ERROR_CODE) {
    return {
      kind: 'content-rejected',
      message: CONTENT_REJECTED_MESSAGE,
      flaggedText: body.flaggedText,
    };
  }

  if (error.response.status === UNAUTHORIZED_STATUS) {
    return { kind: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
  }

  // Only a 400 body is written for the reviewer. A 5xx body is an internal failure report — echoing
  // it puts driver stack traces and upstream host names on the customer's screen.
  if (error.response.status === BAD_REQUEST_STATUS) {
    return { kind: 'unknown', message: toServerMessage(body) ?? UNKNOWN_MESSAGE };
  }

  return { kind: 'unknown', message: UNKNOWN_MESSAGE };
};
